import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Plus, Pencil, Eye, Clock, ChevronRight } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatCaseNumber } from "../lib/caseFormat";
import CaseStatusPill from "../components/case/CaseStatusPill";

const formatRelative = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const FILTERS = [
  { v: "all", k: "agent_filter_all" },
  { v: "draft", k: "agent_filter_draft" },
  { v: "submitted", k: "agent_filter_submitted" },
  { v: "under_review", k: "agent_filter_under_review" },
  { v: "approved", k: "agent_filter_approved" },
  { v: "paid", k: "agent_filter_paid" },
  { v: "pending_re_edit", k: "agent_filter_pending_re_edit" },
  { v: "rejected", k: "agent_filter_rejected" },
  { v: "closed", k: "agent_filter_closed" },
];

const AgentDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftCases = cases.filter((c) => c.status === "draft");
  const latestDraft = draftCases.length ? draftCases[0] : null;

  const filtered = filter === "all" ? cases : cases.filter((c) => c.status === filter);

  // Phase 9: only drafts are editable for the agent.
  const isEditable = (status) => status === "draft";

  const [jump, setJump] = useState("");
  const onJump = (e) => {
    e.preventDefault();
    const trimmed = jump.trim();
    if (!trimmed) return;
    const wanted = parseInt(trimmed, 10);
    if (Number.isNaN(wanted)) return;
    const hit = cases.find((c) => parseInt(c.case_number, 10) === wanted);
    if (hit) {
      navigate(isEditable(hit.status) ? `/agent/cases/${hit.id}/edit` : `/agent/cases/${hit.id}`);
    } else {
      toast.error(t("case_search_not_found"));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="agent-dashboard">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">
              Agent · {user?.agent_number || "—"}
            </p>
            <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
              {t("agent_welcome")}
              {user?.name}
            </h2>
            <p className="text-slate-500 text-sm mt-1">{t("agent_my_cases")}</p>
          </div>
          <Button
            onClick={() => navigate("/agent/cases/new")}
            className="bg-[#0A2540] hover:bg-[#051626] text-white h-10"
            data-testid="new-case-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("new_case")}
          </Button>
        </div>

        {/* Phase 9: needs-correction banner removed */}
        {latestDraft ? (
          <button
            type="button"
            onClick={() => navigate(`/agent/cases/${latestDraft.id}/edit`)}
            className="w-full text-left bg-gradient-to-r from-[#0A2540] to-[#0A2540]/90 hover:from-[#051626] hover:to-[#051626] text-white rounded-lg shadow-sm transition-colors group"
            data-testid="continue-draft-banner"
          >
            <div className="flex items-center gap-4 px-6 py-5">
              <div className="h-10 w-10 rounded-md bg-white/15 flex items-center justify-center">
                <Clock className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">
                  {t("agent_continue_draft_title")}
                </p>
                <p className="font-display text-lg font-medium mt-0.5">
                  {latestDraft.applicant_name || t("case_form_new_title")}
                </p>
                <p className="text-xs text-white/70 mt-0.5">
                  {t("agent_continue_draft_subtitle")} · {formatRelative(latestDraft.updated_at)}
                </p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1 text-sm text-white/80 group-hover:text-white">
                {t("agent_continue_draft_cta")}
                <ChevronRight className="h-4 w-4" />
              </span>
            </div>
          </button>
        ) : null}

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => {
              const active = filter === f.v;
              return (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => setFilter(f.v)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus-ring ${
                    active
                      ? "bg-[#0A2540] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`agent-filter-${f.v}`}
                >
                  {t(f.k)}
                </button>
              );
            })}
            <form onSubmit={onJump} className="ml-auto flex items-center gap-2">
              <Input
                value={jump}
                onChange={(e) => setJump(e.target.value)}
                placeholder={t("case_search_placeholder")}
                className="h-8 w-44 text-sm"
                data-testid="agent-case-jump-input"
                inputMode="numeric"
              />
            </form>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="agent-cases-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3 text-left w-24">{t("case_number_label")}</th>
                  <th className="px-6 py-3 text-left">{t("agent_table_applicant")}</th>
                  <th className="px-6 py-3 text-left">{t("agent_table_status")}</th>
                  <th className="px-6 py-3 text-left">{t("agent_table_updated")}</th>
                  <th className="px-6 py-3 text-right">{t("agent_table_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                      <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-10 py-16 text-center text-slate-500" data-testid="agent-cases-empty">
                      <FileText className="h-8 w-8 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm">{t("agent_no_cases")}</p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`case-row-${c.id}`}
                    >
                      <td className="px-6 py-4 align-middle text-sm font-mono text-[#0A2540]" data-testid={`case-number-${c.id}`}>
                        {formatCaseNumber(c.case_number)}
                      </td>
                      <td className="px-6 py-4 align-middle">
                        <div className="text-sm font-medium text-[#0A2540]">
                          {c.applicant_name || (
                            <span className="text-slate-400 italic">
                              {t("case_form_new_title")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 align-middle">
                        <CaseStatusPill status={c.status} />
                      </td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-500">
                        {formatRelative(c.updated_at)}
                      </td>
                      <td className="px-6 py-4 align-middle text-right">
                        {isEditable(c.status) ? (
                          <Link
                            to={`/agent/cases/${c.id}/edit`}
                            className="inline-flex items-center text-sm font-medium text-[#0A2540] hover:text-[#007AFF] focus-ring rounded-md px-2 py-1"
                            data-testid={`edit-case-${c.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            {t("agent_action_continue")}
                          </Link>
                        ) : (
                          <Link
                            to={`/agent/cases/${c.id}`}
                            className="inline-flex items-center text-sm font-medium text-[#0A2540] hover:text-[#007AFF] focus-ring rounded-md px-2 py-1"
                            data-testid={`view-case-${c.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t("agent_action_view")}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentDashboard;
